// CLI v0.8 Module 3 — `pollDeploy` service (Task M3.3).
//
// Polls `GET /deploys/:trace_id` until the deploy reaches a terminal status
// (`'success'` | `'failed'`) and returns the final trace. Intermediate state
// is forwarded to a caller-supplied `onPoll` callback so verb code can pipe
// it into a `MultiStepRenderer` without this service knowing about
// terminal/CI/TTY concerns.
//
// Wire shape: see `packages/api/src/routes/deploys.ts` — `/deploys/:trace_id`
// is registered top-level, NOT under `/api/v2/`. Hence we call
// `client.get('/deploys/' + traceId)` rather than prefixing `/api/v2`.
//
// Why polling and not SSE: the server has no SSE endpoint for deploy traces.
// Builds + rollouts are observable only via this polling endpoint.
//
// Failure model:
// - V2ApiError (HTTP 4xx/5xx with envelope) is thrown immediately. That's a
//   real server response, not a transient blip; retrying would hide bugs.
// - Network/fetch errors (anything that's NOT a V2ApiError) increment a
//   counter; we tolerate up to `maxConsecutiveFailures` consecutive failures
//   before throwing `PollNetworkLostError`. The counter resets on success.
// - Hard timeout via `maxDurationMs` throws `PollTimeoutError`. The deploy
//   itself is still in flight on the server; the user can re-attach with
//   `grid plug --attach <trace_id>` (per the v0.8 plan).
// - `signal` lets the verb interrupt cleanly (e.g. SIGINT). Throws
//   `PollAbortedError`.

import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import type { Step } from '../render/multi-step.js';
import { CloudGridError } from '../utils.js';
import type { DeployEventError } from '../../../shared/src/deploy-event-error.js';

// ─────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────

/**
 * The shape of `DeployTrace.error`. Either the new structured payload
 * (backlog 187, every field present) OR — for endpoints that still hand
 * back the legacy two-field shape — `code` + `message` + optional
 * `failed_step`. The structured fields are optional in the type so legacy
 * payloads still parse; the renderer prefers them when present.
 */
export type DeployTraceError =
  | (DeployEventError & { message?: string; failed_step?: string })
  | { code: string; message: string; failed_step?: string };

export interface DeployTrace {
  trace_id: string;
  entity: { id: string; name: string; namespace: string; org_id: string; org_slug: string };
  status: 'queued' | 'building' | 'deploying' | 'success' | 'failed' | string;
  started_at: string | null;
  completed_at: string | null;
  steps: Step[];
  /**
   * Backlog 187 — failed deploys return a structured `DeployEventError`
   * with user-language messaging + categorization. The legacy two-field
   * shape (`code` + `message`) is kept around as the runtime read shape
   * so plug renderers that haven't been updated yet still work — the new
   * structured fields are additive. The `routes/deploys.ts` shim wraps
   * pre-187 string-only errors so the CLI never sees the raw legacy form.
   */
  error: DeployTraceError | null;
  result: {
    url: string;
    legacyUrl?: string;
    services?: Array<{ name: string; image: string; ready: boolean }>;
  } | null;
  http_status: number | null;
}

export interface PollDeployOpts {
  /** Polling cadence between successful polls. Default 2000ms. */
  pollIntervalMs?: number;
  /** Hard timeout. Default 20 minutes (`DEFAULT_MAX_DURATION_MS`). */
  maxDurationMs?: number;
  /** Consecutive network failures tolerated before giving up. Default 3. */
  maxConsecutiveFailures?: number;
  /** Cancellation. Checked at the top of every iteration and after sleeps. */
  signal?: AbortSignal;
  /** Called once after every successful poll, with the full trace. */
  onPoll?: (trace: DeployTrace) => void;
  /** Sleep adapter for tests — defaults to setTimeout-backed delay. */
  sleep?: (ms: number) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────

/**
 * Backlog 477 — did the Cloud Build finish successfully by the time the poll
 * gave up? When yes, the timeout is "build done, rollout still in progress",
 * NOT "build failed" — the message must say so. The signal is the
 * `cloud_build` step reaching `ok`, OR the trace having progressed past
 * `building` (status `deploying` / `success` both imply the build landed).
 */
export function buildSucceededAtTimeout(trace: DeployTrace | undefined): boolean {
  if (!trace) return false;
  if (trace.status === 'deploying' || trace.status === 'success') return true;
  const cb = trace.steps?.find((s) => s.step === 'cloud_build');
  return cb?.status === 'ok';
}

/**
 * Backlog 544 Fix 3 — classify a poll timeout so the message and exit code
 * reflect what was actually happening server-side, instead of a flat
 * "did not finish" hard failure:
 *
 * - `build_succeeded`  — build landed, rollout still pending (BL477).
 * - `building_working` — status `building`, Cloud Build still WORKING and
 *                        long-running (> `CB_LONG_RUNNING_MS`).
 * - `building`         — status `building`, no long-running CB visible.
 * - `hard`             — no recent poll, or the deploy never progressed past
 *                        queue. This is the only kind that stays a hard error.
 *
 * Everything except `hard` is a "soft" timeout: the deploy continues
 * server-side, so the CLI signals a warning (exit 2) rather than a failure.
 */
export type TimeoutKind = 'build_succeeded' | 'building_working' | 'building' | 'hard';

/** Cloud Build step age in ms, preferring `elapsed_ms`, then `started_at`. */
function cloudBuildAgeMs(step: Step | undefined): number {
  if (!step) return 0;
  if (typeof step.elapsed_ms === 'number') return step.elapsed_ms;
  if (typeof step.started_at === 'string') {
    const t = Date.parse(step.started_at);
    if (Number.isFinite(t)) return Date.now() - t;
  }
  return 0;
}

/** Best-effort Cloud Build job id from the trace, or null when unknown. */
function extractBuildId(trace: DeployTrace | undefined): string | null {
  if (!trace) return null;
  const fromResult = (trace.result as { build_id?: unknown } | null)?.build_id;
  if (typeof fromResult === 'string' && fromResult.length > 0) return fromResult;
  const cb = trace.steps?.find((s) => s.step === 'cloud_build');
  const fromStep = (cb as { build_id?: unknown } | undefined)?.build_id;
  if (typeof fromStep === 'string' && fromStep.length > 0) return fromStep;
  return null;
}

export function classifyTimeout(trace: DeployTrace | undefined): TimeoutKind {
  if (!trace) return 'hard';
  if (buildSucceededAtTimeout(trace)) return 'build_succeeded';
  if (trace.status === 'building') {
    const cb = trace.steps?.find((s) => s.step === 'cloud_build');
    if (cb?.status === 'in_progress' && cloudBuildAgeMs(cb) > CB_LONG_RUNNING_MS) {
      return 'building_working';
    }
    return 'building';
  }
  return 'hard';
}

export class PollTimeoutError extends CloudGridError {
  /** Whether the underlying Cloud Build had succeeded when the poll timed out. */
  public readonly buildSucceeded: boolean;
  /** Backlog 544 — classification driving message + exit code. */
  public readonly timeoutKind: TimeoutKind;
  /**
   * Backlog 544 — true when the deploy was still progressing server-side
   * (build succeeded, or still building). The CLI exits 2 (warning) for
   * these instead of 1 (hard fail): nothing is broken, the rollout
   * continues, and the user just needs to check `grid status`.
   */
  public readonly softTimeout: boolean;

  constructor(
    public readonly traceId: string,
    public readonly durationMs: number,
    // Backlog 477 — the last trace observed before the timeout. Lets the
    // message distinguish "build succeeded, rollout still pending" from
    // "the deploy never got anywhere", instead of a flat "did not finish".
    lastTrace?: DeployTrace,
  ) {
    super(PollTimeoutError.composeMessage(traceId, durationMs, lastTrace));
    this.name = 'PollTimeoutError';
    this.buildSucceeded = buildSucceededAtTimeout(lastTrace);
    this.timeoutKind = classifyTimeout(lastTrace);
    this.softTimeout = this.timeoutKind !== 'hard';
  }

  private static composeMessage(
    traceId: string,
    durationMs: number,
    lastTrace?: DeployTrace,
  ): string {
    const kind = classifyTimeout(lastTrace);
    // Prefer the entity name; fall back to the trace id.
    const name = lastTrace?.entity?.name || traceId;
    switch (kind) {
      case 'build_succeeded':
        // §23 voice: plain, specific, no alarm. The build is done; the
        // rollout is still landing — point the user at the status command
        // rather than implying failure.
        return (
          `Build finished — rollout still in progress. ` +
          `Check \`grid status ${name}\`.`
        );
      case 'building_working': {
        // Build is genuinely still running. Tell the user it will continue
        // and that no rollback is needed — the CLI just ran out of patience.
        const buildId = extractBuildId(lastTrace);
        const jobClause = buildId ? ` (Cloud Build job ${buildId})` : '';
        return (
          `Build is still in progress${jobClause}. ` +
          `Run \`grid status ${name}\` to check, or come back in a few ` +
          `minutes — the deployment will continue. The CLI's wait budget ` +
          `elapsed; no rollback needed.`
        );
      }
      case 'building':
        return (
          `Plug taking longer than expected — ` +
          `run \`grid status ${name}\` for the live state.`
        );
      case 'hard':
      default:
        return `Deploy ${traceId} did not finish within ${durationMs}ms.`;
    }
  }
}

export class PollNetworkLostError extends CloudGridError {
  constructor(public readonly traceId: string, public readonly attempts: number) {
    super(`Lost contact with the API while polling ${traceId} (${attempts} consecutive failures).`);
    this.name = 'PollNetworkLostError';
  }
}

export class PollAbortedError extends CloudGridError {
  constructor(public readonly traceId: string) {
    super(`Polling aborted for ${traceId}.`);
    this.name = 'PollAbortedError';
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_POLL_INTERVAL_MS = 2000;
// Backlog 544 — bumped 10m → 20m. A cold build with image push for a
// medium-sized app (Console-scale) routinely runs ~11–13 minutes; the old
// 10-minute budget had the CLI quit ~49s before Cloud Build finished,
// orphaning a deploy that actually succeeded. 20m rides out a normal cold
// build while still detecting a genuinely hung process. Exported so
// `watch.ts` shares the exact same default (it previously used 30m on the
// same poller — backlog 544 Fix 2). Override per-call via `--timeout`.
export const DEFAULT_MAX_DURATION_MS = 20 * 60 * 1000;
const DEFAULT_MAX_FAILURES = 3;

// Backlog 544 Fix 3 — a Cloud Build that has been WORKING for longer than
// this when the poll budget elapses is "still legitimately building", not
// hung. Drives the more specific "Build is still in progress (Cloud Build
// job …)" message vs the generic "taking longer than expected" line.
const CB_LONG_RUNNING_MS = 5 * 60 * 1000;

// Backlog 099 Phase 1 — fail-fast on every terminal-failure status the API
// might emit, including finer-grained failures the deployer may surface
// later (`rollout_failed`, `build_failed`, …) before its outer catch lands
// the generic `'failed'`. Pre-099, the CLI only knew about `'failed'`, so
// when the API marked the trace terminal mid-flight under one of these
// names the developer's terminal kept spinning until the 10-minute wall
// clock, even though Slack already said it failed.
const TERMINAL_FAILURE_STATUSES = [
  'failed',
  'rollout_failed',
  'build_failed',
  'dns_failed',
  'probe_failed',
  'apply_failed',
  'validation_failed',
] as const;

const TERMINAL_STATUSES = new Set<string>([
  'success',
  ...TERMINAL_FAILURE_STATUSES,
]);

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll `GET /deploys/<traceId>` until terminal. Returns the final trace.
 *
 * Throws:
 * - `PollTimeoutError` — `maxDurationMs` exceeded.
 * - `PollNetworkLostError` — `maxConsecutiveFailures` consecutive transport errors.
 * - `PollAbortedError` — caller's `signal` was aborted.
 * - `V2ApiError` — passed through unchanged when the server responds 4xx/5xx.
 */
export async function pollDeploy(
  client: IV2Client,
  traceId: string,
  opts: PollDeployOpts = {},
): Promise<DeployTrace> {
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxDurationMs = opts.maxDurationMs ?? DEFAULT_MAX_DURATION_MS;
  const maxConsecutiveFailures = opts.maxConsecutiveFailures ?? DEFAULT_MAX_FAILURES;
  const sleep = opts.sleep ?? defaultSleep;
  const signal = opts.signal;
  const onPoll = opts.onPoll;

  const startedAt = Date.now();
  let consecutiveFailures = 0;
  // Backlog 477 — remember the most recent successful poll so a timeout can
  // report whether the build had already succeeded (rollout pending) vs the
  // deploy never progressing.
  let lastTrace: DeployTrace | undefined;

  // Top-of-loop checks: respect cancellation and the hard timeout BEFORE
  // dispatching the next request, so we don't waste a network round-trip
  // when we already know we have to give up.
  // Loop until we return (terminal) or throw.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) throw new PollAbortedError(traceId);
    if (Date.now() - startedAt > maxDurationMs) {
      throw new PollTimeoutError(traceId, maxDurationMs, lastTrace);
    }

    let pollResult: DeployTrace | undefined;
    try {
      pollResult = await client.get<DeployTrace>(`/deploys/${traceId}`);
    } catch (err) {
      // V2ApiError is a real server response — surface it without retry.
      // Anything else is a transport error (DNS, TCP, fetch failure, etc.).
      if (err instanceof V2ApiError) throw err;
      consecutiveFailures++;
      if (consecutiveFailures >= maxConsecutiveFailures) {
        throw new PollNetworkLostError(traceId, consecutiveFailures);
      }
      await sleep(pollIntervalMs);
      continue;
    }

    consecutiveFailures = 0;
    lastTrace = pollResult;
    onPoll?.(pollResult);
    if (TERMINAL_STATUSES.has(pollResult.status)) return pollResult;

    await sleep(pollIntervalMs);
  }
}
